// The input controls both tools share: a drop zone that also takes a click or a
// paste, and the colour swatch picker beside it.
//
// Dropzone.js is already on the page from the header, but it exists to POST
// files somewhere. These tools must not send anything, so they take the files
// straight out of the event instead.

/**
 * Wire a drop zone.
 *
 * @param {object}   options
 * @param {Element}  options.zone      Element that accepts drops and clicks.
 * @param {Element}  options.input     The <input type="file"> behind it.
 * @param {boolean}  options.multiple  Whether more than one file is useful.
 * @param {Function} options.onFiles   Called with an array of image Files.
 * @returns {Function} Removes the paste listener, which is the only one that
 *                     outlives the elements.
 */
export function wirePicker({ zone, input, multiple = false, onFiles }) {
    const imagesOnly = files => Array.from(files).filter(file => file.type.startsWith('image/'));

    // Whatever is left out is said, in a toast: the drop zone is often hidden
    // by then, and a file that silently does nothing reads as a broken tool.
    const tell = message => {
        if (window.ui && typeof window.ui.toast === 'function') window.ui.toast(message, 'warning');
    };

    const hand = files => {
        const all = Array.from(files);
        const images = imagesOnly(all);
        const skipped = all.length - images.length;
        if (skipped) {
            tell(skipped === 1 && all.length === 1
                ? `${all[0].name} is not an image, so there is nothing to do with it here.`
                : `${skipped} of ${all.length} files are not images and were left out.`);
        }
        if (!images.length) return;
        if (!multiple && images.length > 1) {
            tell(`This tool works on one image at a time, so only ${images[0].name} was opened.`);
        }
        onFiles(multiple ? images : [images[0]]);
    };

    zone.addEventListener('click', event => {
        // input.click() dispatches a click that bubbles back here if the input
        // sits inside the zone, and opening the picker from its own click event
        // never stops.
        if (event.target === input) return;
        input.click();
    });

    zone.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            input.click();
        }
    });

    input.addEventListener('change', () => {
        hand(input.files);
        // Cleared so that picking the same file twice in a row still fires a
        // change event, which is what someone does after tweaking a setting.
        input.value = '';
    });

    // dragenter and dragleave fire for every child element the pointer crosses,
    // so a counter decides when the pointer has really left rather than the
    // events alone.
    let depth = 0;
    const setDragging = on => zone.classList.toggle('is-dragging', on);

    zone.addEventListener('dragenter', event => {
        event.preventDefault();
        depth += 1;
        setDragging(true);
    });

    zone.addEventListener('dragover', event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    });

    zone.addEventListener('dragleave', () => {
        depth = Math.max(0, depth - 1);
        if (depth === 0) setDragging(false);
    });

    zone.addEventListener('drop', event => {
        event.preventDefault();
        depth = 0;
        setDragging(false);
        hand(event.dataTransfer.files);
    });

    // A screenshot on the clipboard is the shortest path into either tool, so
    // the whole document listens rather than only the focused zone.
    const onPaste = event => {
        const files = Array.from(event.clipboardData?.files || []);
        if (files.length) {
            event.preventDefault();
            hand(files);
        }
    };
    document.addEventListener('paste', onPaste);

    return () => document.removeEventListener('paste', onPaste);
}

/**
 * Stop the browser from opening a file that was dropped next to the zone
 * rather than on it, which navigates away and loses whatever was in progress.
 */
export function blockStrayDrops() {
    for (const type of ['dragover', 'drop']) {
        window.addEventListener(type, event => {
            const target = event.target;
            if (target instanceof Element && target.closest('[data-picker-zone]')) return;
            event.preventDefault();
        });
    }
}

/**
 * Turn the colour inputs on the page into swatch pickers.
 *
 * Coloris comes from the shared header with `defer`, so it has always run by
 * the time the tools start. If it ever does not, the inputs stay plain text
 * fields holding a hex value, which both tools read the same way.
 */
export function initColourPicker() {
    if (typeof window.Coloris === 'undefined') return;
    window.Coloris({
        el: '[data-coloris]',
        theme: 'pill',
        themeMode: 'auto',
        alpha: false,
        swatches: [
            '#ffffff', '#e2e8f0', '#94a3b8', '#334155', '#0f172a',
            '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#14b8a6',
            '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'
        ]
    });
}
